import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { AreaChart, Area, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import NativeConnections from "../components/NativeConnections";
import TestEmailCard from "../components/TestEmailCard";
import MigrationPanel from "../components/MigrationPanel";
import AdminWatermarkCard from "../components/AdminWatermarkCard";
import NotificationsPanel from "../components/NotificationsPanel";
import { Instagram, Facebook, Music2, Youtube, Twitter, Linkedin, Plug, Unplug, Sparkles, TrendingUp, Users, Eye, Save, PauseCircle, Zap, RefreshCw, HeartPulse, AlertTriangle, ImageDown, MessageCircle, Cloud, CloudUpload, Trash2, HardDrive } from "lucide-react";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "tiktok", label: "TikTok", icon: Music2 },
  { id: "youtube", label: "YouTube", icon: Youtube },
  { id: "twitter", label: "X / Twitter", icon: Twitter },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
];

export default function Admin() {
  const { selected, brands, refreshBrands } = useBrand();
  const [accounts, setAccounts] = useState([]);
  const [perf, setPerf] = useState(null);
  const [queue, setQueue] = useState([]);
  const [form, setForm] = useState(null);
  const [ayr] = useState({ configured: false });  // legacy — kept to avoid null refs in a few JSX branches while we finish the Ayrshare rip-out
  const [health, setHealth] = useState([]);
  const [publog, setPublog] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [integrationsBusy, setIntegrationsBusy] = useState(false);
  const loadIntegrations = () => {
    setIntegrationsBusy(true);
    api.get("/integrations/status")
      .then((r) => setIntegrations(r.data))
      .catch(() => setIntegrations(null))
      .finally(() => setIntegrationsBusy(false));
  };
  useEffect(() => { loadIntegrations(); }, []);

  const rescanMetaPages = async () => {
    const wait = toast.loading("Re-scanning Meta pages from token…");
    try {
      const { data } = await api.post("/integrations/native/meta/rescan-pages");
      const dp = data.denzel_pheonix_detected;
      toast.success(`${data.total} pages found${dp ? ` · Denzel Pheonix (${data.denzel_pheonix_name}) detected ✓` : " · Denzel Pheonix NOT found"}`, { id: wait, duration: 8000 });
      loadIntegrations();
    } catch (e) {
      toast.error(errText(e), { id: wait });
    }
  };
  const [ctaBusy, setCtaBusy] = useState(false);
  const [oldUrl, setOldUrl] = useState("");
  const [ctaJob, setCtaJob] = useState(null);
  const [ctaScope, setCtaScope] = useState("companions");
  const activeBrand = brands.find((b) => b.name === selected);

  const [thumbBusy, setThumbBusy] = useState(false);
  const [thumbResult, setThumbResult] = useState(null);
  const [thumbScope, setThumbScope] = useState("brand");
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentResult, setCommentResult] = useState(null);
  const backfillFirstComments = async () => {
    if (thumbScope === "brand" && !activeBrand?.name) return toast.error("Select a brand first");
    const target = thumbScope === "brand" ? activeBrand.name : "every FB/IG post in this workspace";
    if (!window.confirm(`Post the CTA first-comment on published posts in ${target} that were missing one?\n\nUses the Meta comments edge (same call the publish worker now runs on new publishes). Skips posts that already have a first comment.`)) return;
    setCommentBusy(true);
    setCommentResult(null);
    const wait = toast.loading(`Backfilling first-comments for ${target}…`);
    try {
      const params = thumbScope === "brand" ? `?brand=${encodeURIComponent(activeBrand.name)}` : "";
      const { data } = await api.post(`/posts/backfill-first-comments${params}`);
      setCommentResult(data);
      const parts = [`${data.updated}/${data.scanned} comments posted`];
      if (data.orphaned) parts.push(`${data.orphaned} orphan${data.orphaned > 1 ? "s" : ""} skipped`);
      if (data.failed) parts.push(`${data.failed} failed`);
      toast.success(parts.join(" · "), { id: wait, duration: 10000 });
    } catch (e) {
      toast.error(errText(e), { id: wait });
    }
    setCommentBusy(false);
  };
  const backfillFbThumbs = async () => {
    // "brand" scope filters to the currently selected sub-brand; "umbrella"
    // sweeps every historic FB Reel/video across the workspace in one pass.
    if (thumbScope === "brand" && !activeBrand?.name) return toast.error("Select a brand first");
    const target = thumbScope === "brand" ? activeBrand.name : "every FB post in this workspace";
    if (!window.confirm(`Re-apply the latest local video posters to ${target} on Facebook?\n\nEach video will get its "preferred" thumbnail refreshed via the Meta Video Thumbnails edge — this is the only reliable way to fix black posters on Reels that Meta locked in at first publish. Takes ~1s per post.`)) return;
    setThumbBusy(true);
    setThumbResult(null);
    const wait = toast.loading(`Backfilling FB thumbnails for ${target}…`);
    try {
      const params = thumbScope === "brand" ? `?brand=${encodeURIComponent(activeBrand.name)}` : "";
      const { data } = await api.post(`/posts/fix-fb-thumbnails${params}`);
      setThumbResult(data);
      const parts = [`${data.updated}/${data.scanned} thumbnails refreshed`];
      if (data.orphaned) parts.push(`${data.orphaned} orphan${data.orphaned > 1 ? "s" : ""} skipped`);
      if (data.failed) parts.push(`${data.failed} failed`);
      toast.success(parts.join(" · "), { id: wait, duration: 10000 });
    } catch (e) {
      toast.error(errText(e), { id: wait });
    }
    setThumbBusy(false);
  };

  const refreshCompanionCtas = async () => {
    if (!activeBrand?.id) return toast.error("Select a brand first");
    const label = ctaScope === "all" ? "every post" : "companion posts";
    if (!window.confirm(`Refresh CTAs on ${label} for ${activeBrand.name}?${oldUrl.trim() ? `\n\nAny occurrence of "${oldUrl.trim()}" will be swapped for the current brand URL.` : ""}\n\nThis rewrites copy + first_comment on past posts using the LATEST brand knowledge (URL, launch stage, CTA rules). The rest of every post is preserved.\n\nRuns in the background — you can leave this page.`)) return;
    setCtaBusy(true);
    setCtaJob(null);
    const wait = toast.loading(`Starting CTA refresh for ${activeBrand.name}…`);
    try {
      const { data } = await api.post(`/brands/${activeBrand.id}/refresh-ctas`, {
        old_url: oldUrl.trim() || null, scope: ctaScope,
      });
      if (data.status === "empty") {
        toast.info(`No matching posts to refresh for ${activeBrand.name}`, { id: wait });
        setCtaBusy(false);
        return;
      }
      toast.success(`Refreshing ${data.total} posts in the background…`, { id: wait, duration: 5000 });
      setCtaJob({ id: data.job_id, status: "queued", total: data.total, updated: 0, failed: 0, url_swapped: 0 });
      const poll = async () => {
        try {
          const { data: j } = await api.get(`/cta-refresh-jobs/${data.job_id}`);
          setCtaJob(j);
          if (j.status === "complete" || j.status === "failed") {
            setCtaBusy(false);
            toast.success(`Done — ${j.updated}/${j.total} posts refreshed${j.url_swapped ? ` · ${j.url_swapped} URL swaps` : ""}${j.failed ? ` · ${j.failed} failed` : ""}`, { duration: 10000 });
            setOldUrl("");
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          setCtaBusy(false);
          toast.error(errText(e));
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(errText(e), { id: wait });
      setCtaBusy(false);
    }
  };

  const loadAll = () => {
    api.get("/social-accounts", { params: selected ? { brand: selected } : {} }).then((r) => setAccounts(r.data));
    api.get("/analytics/performance", { params: selected ? { brand: selected } : {} }).then((r) => setPerf(r.data));
    api.get("/posts", { params: selected ? { brand: selected } : {} }).then((r) => setQueue(r.data.filter((p) => ["scheduled", "approved", "pending_review"].includes(p.review_status))));
    api.get("/brands/health").then((r) => setHealth(r.data));
    api.get("/posts/publish-log", { params: selected ? { brand: selected } : {} }).then((r) => setPublog(r.data));
  };
  useEffect(loadAll, [selected]);

  useEffect(() => {
    if (activeBrand) {
      setForm({
        status: activeBrand.status || "active", voice: activeBrand.voice || "", target_audience: activeBrand.target_audience || "",
        default_cta: activeBrand.default_cta || "", disclaimer: activeBrand.disclaimer || "",
        tag_bank: (activeBrand.tag_bank || []).join(", "), paused: !!activeBrand.paused,
        posting_time: activeBrand.posting_time || "10:00",
        pillars: activeBrand.pillars || [],
        sample_workout_url: activeBrand.sample_workout_url || "",
      });
    } else setForm(null);
  }, [selected, brands]);

  const connect = async (platform) => {
    try {
      await api.post("/social-accounts", { platform, brand: selected || null });
      toast.success(`${platform} connected (simulated OAuth) — content will route here`);
      loadAll();
    } catch (e) { toast.error(errText(e)); }
  };
  const disconnect = async (acc) => {
    await api.delete(`/social-accounts/${acc.id}`);
    toast.success(`${acc.platform} disconnected`);
    loadAll();
  };

  const saveIdentity = async () => {
    try {
      await api.patch(`/brands/${activeBrand.id}`, {
        status: form.status, voice: form.voice, target_audience: form.target_audience,
        default_cta: form.default_cta, disclaimer: form.disclaimer,
        tag_bank: form.tag_bank.split(",").map((t) => t.trim().replace(/^#/, "")).filter(Boolean),
        paused: form.paused, posting_time: form.posting_time, pillars: form.pillars,
        sample_workout_url: form.sample_workout_url || null,
      });
      refreshBrands();
      toast.success(`${activeBrand.name} identity & safety settings saved`);
    } catch (e) { toast.error(errText(e)); }
  };

  const accFor = (pid) => accounts.find((a) => a.platform === pid && a.status === "connected");

  const brandHealth = health.find((h) => (h.brand || "") === selected);

  return (
    <div className="space-y-8 fade-up" data-testid="admin-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Admin · {selected || "Harmony Wellness Group (umbrella)"}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Master control room.</h1>
        <div className="text-muted-foreground mt-1 text-sm flex items-center flex-wrap gap-2">
          <span>Use the sidebar brand switcher to choose which workspace you're administering.</span>
          {activeBrand?.status === "draft" && <Badge variant="secondary">Draft brand</Badge>}
          {brandHealth && <Badge variant="outline" data-testid="admin-health-chip" className={`${brandHealth.score >= 75 ? "text-accent border-accent/50" : brandHealth.score >= 40 ? "text-amber-600 border-amber-500/50" : "text-destructive border-destructive/50"}`}><HeartPulse size={11} className="mr-1" /> Knowledge health {brandHealth.score}/100</Badge>}
        </div>
      </div>

      <Tabs defaultValue="accounts">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="accounts" data-testid="admin-tab-accounts">Connected accounts</TabsTrigger>
          <TabsTrigger value="identity" data-testid="admin-tab-identity">Identity & safety</TabsTrigger>
          <TabsTrigger value="queue" data-testid="admin-tab-queue">Content queue</TabsTrigger>
          <TabsTrigger value="publishlog" data-testid="admin-tab-publishlog">Publish log</TabsTrigger>
          <TabsTrigger value="performance" data-testid="admin-tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="migration" data-testid="admin-tab-migration">Migration</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts" className="pt-6 space-y-6">
          <Card data-testid="integrations-health-card">
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
              <div>
                <CardTitle className="font-heading text-xl flex items-center gap-2"><Zap size={18} /> Integrations status & spend</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Every service this app talks to, at a glance. Cost columns are rolling 30-day estimates based on public unit pricing — real invoices are always the source of truth.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {integrations && (
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">30-day spend</p>
                    <p className="font-heading text-2xl leading-none" data-testid="integrations-total-cost">${integrations.totals?.estimated_cost_30d_usd?.toFixed?.(2) ?? "0.00"}</p>
                  </div>
                )}
                <Button size="sm" variant="outline" data-testid="integrations-refresh-btn" onClick={loadIntegrations} disabled={integrationsBusy}>
                  <RefreshCw size={13} className={`mr-1 ${integrationsBusy ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!integrations && integrationsBusy && <p className="text-sm text-muted-foreground">Loading integration status…</p>}
              {!integrations && !integrationsBusy && <p className="text-sm text-destructive">Could not load integration status.</p>}
              {integrations && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {integrations.integrations.map((it) => {
                    const STATUS = {
                      ok: { c: "bg-emerald-500", l: "OK" }, connected: { c: "bg-emerald-500", l: "OK" },
                      no_data: { c: "bg-slate-400", l: "No data yet" },
                      degraded: { c: "bg-amber-500", l: "Degraded" }, expiring: { c: "bg-amber-500", l: "Expiring" },
                      critical: { c: "bg-red-500", l: "Critical" }, expired: { c: "bg-red-500", l: "Expired" }, error: { c: "bg-red-500", l: "Error" },
                      not_connected: { c: "bg-slate-400", l: "Not connected" }, no_key: { c: "bg-slate-400", l: "No key" }, not_configured: { c: "bg-slate-400", l: "Not configured" },
                    };
                    const s = STATUS[it.status] || { c: "bg-slate-400", l: it.status };
                    return (
                      <div key={it.key} className="rounded-lg border border-border p-4 space-y-2" data-testid={`integration-card-${it.key}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">{it.name}</p>
                            <p className="text-[11px] text-muted-foreground line-clamp-2">{it.purpose}</p>
                          </div>
                          <Badge className={`${s.c} text-white border-0 text-[10px] shrink-0`} data-testid={`integration-status-${it.key}`}>{s.l}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                          {it.key === "meta" && (
                            <>
                              <div><span className="text-muted-foreground">Pages:</span> <span className="font-semibold">{it.pages?.length || 0}</span></div>
                              <div><span className="text-muted-foreground">Published 7d:</span> <span className="font-semibold">{it.activity_7d?.published ?? 0}</span> · {it.activity_7d?.failed ?? 0} failed</div>
                              <div className="col-span-2 flex items-center gap-2 pt-1">
                                <span className="text-muted-foreground">Denzel Pheonix cross-post:</span>
                                {it.denzel_pheonix_detected
                                  ? <Badge className="bg-emerald-500 text-white border-0 text-[10px]">{it.denzel_pheonix_name} · {it.denzel_pheonix_success_7d} sent · {it.denzel_pheonix_failed_7d} failed</Badge>
                                  : <Badge className="bg-slate-400 text-white border-0 text-[10px]">Not detected</Badge>}
                              </div>
                            </>
                          )}
                          {it.key === "veo" && (
                            <>
                              <div><span className="text-muted-foreground">Scenes 30d:</span> <span className="font-semibold">{it.scenes_rendered_30d}/{it.scenes_total_30d}</span></div>
                              <div><span className="text-muted-foreground">Fallback rate:</span> <span className={`font-semibold ${it.fallback_rate_30d_pct > 40 ? "text-red-500" : it.fallback_rate_30d_pct > 10 ? "text-amber-500" : "text-emerald-500"}`}>{it.fallback_rate_30d_pct}%</span></div>
                            </>
                          )}
                          {it.key === "gemini_image" && <div><span className="text-muted-foreground">Images 30d:</span> <span className="font-semibold">{it.images_30d}</span></div>}
                          {it.key === "gemini_tts" && <div><span className="text-muted-foreground">Narrated 30d:</span> <span className="font-semibold">{it.posts_narrated_30d}</span></div>}
                          {it.key === "whisper" && <div><span className="text-muted-foreground">Captioned 30d:</span> <span className="font-semibold">{it.captioned_30d}</span></div>}
                          {it.key === "resend" && <div><span className="text-muted-foreground">Emails 7d:</span> <span className="font-semibold">{it.emails_7d}</span></div>}
                          <div className="col-span-2 border-t border-border/50 pt-1.5 mt-1 flex justify-between">
                            <span className="text-muted-foreground">Est. 30d cost</span>
                            <span className="font-semibold">${(it.cost_30d_usd || 0).toFixed(2)}</span>
                          </div>
                        </div>
                        {(it.notes || []).length > 0 && (
                          <div className="pt-2 space-y-1">
                            {it.notes.map((n, i) => (
                              <p key={i} className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1">
                                <AlertTriangle size={11} className="shrink-0 mt-0.5" /> {n}
                              </p>
                            ))}
                          </div>
                        )}
                        {it.key === "meta" && !it.denzel_pheonix_detected && it.connected && (
                          <Button size="sm" variant="outline" className="mt-1 w-full" data-testid="rescan-pages-btn" onClick={rescanMetaPages}>
                            <RefreshCw size={12} className="mr-1.5" /> Re-scan Meta pages
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <NativeConnections />

          <TestEmailCard />

          <Card data-testid="fb-thumbs-backfill-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2"><ImageDown size={18} /> Backfill Facebook thumbnails</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Older Reels shipped with black poster frames because Meta silently ignored our custom-thumbnail URL on Reel-classified videos. This job re-applies the latest local poster JPEG to each historic FB post using the Video Thumbnails edge (the only path Meta honours for Reels). Runs synchronously — expect ~1s per post.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex rounded-md border overflow-hidden">
                  <button type="button" data-testid="fb-thumbs-scope-brand"
                    onClick={() => setThumbScope("brand")}
                    className={`text-xs px-3 py-1.5 ${thumbScope === "brand" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
                    This brand{activeBrand?.name ? ` · ${activeBrand.name}` : ""}
                  </button>
                  <button type="button" data-testid="fb-thumbs-scope-umbrella"
                    onClick={() => setThumbScope("umbrella")}
                    className={`text-xs px-3 py-1.5 border-l ${thumbScope === "umbrella" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
                    Every brand
                  </button>
                </div>
                <Button data-testid="fb-thumbs-backfill-btn" onClick={backfillFbThumbs} disabled={thumbBusy}>
                  {thumbBusy ? <RefreshCw size={13} className="mr-1.5 animate-spin" /> : <ImageDown size={13} className="mr-1.5" />}
                  {thumbBusy ? "Refreshing…" : "Refresh FB thumbnails"}
                </Button>
              </div>
              {thumbResult && (
                <div className="text-xs text-muted-foreground border rounded-md p-3 bg-muted/40" data-testid="fb-thumbs-result">
                  Scanned <b className="text-foreground">{thumbResult.scanned}</b> · Updated <b className="text-accent">{thumbResult.updated}</b>
                  {thumbResult.orphaned > 0 && <> · Skipped orphans <b className="text-foreground">{thumbResult.orphaned}</b></>}
                  {thumbResult.failed > 0 && <> · Failed <b className="text-destructive">{thumbResult.failed}</b></>}
                  {thumbResult.orphaned > 0 && (
                    <p className="mt-1.5 text-[11px] italic">
                      Orphans are posts whose Facebook video was deleted on Meta's side — they've been flagged locally and won't be retried.
                    </p>
                  )}
                  {thumbResult.errors?.length > 0 && (
                    <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                      {thumbResult.errors.slice(0, 8).map((err, i) => (
                        <li key={i} className="text-[11px] text-destructive font-mono">{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground italic">
                Prefer to fix a single post? Tap the small 🔄 icon on that post's video thumbnail in Review Feed — it re-rolls the poster locally AND pushes it to Meta if the post is already live.
              </p>

              <div className="pt-4 mt-3 border-t border-dashed space-y-3">
                <div>
                  <h4 className="text-sm font-heading font-semibold flex items-center gap-1.5"><MessageCircle size={14} /> Backfill CTA first-comments</h4>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    New publishes now post the CTA `first_comment` automatically. This one-off job walks every published FB/IG post whose first-comment was skipped by the previous simulation-only code path and posts it now via the Meta comments edge. Uses the scope toggle above.
                  </p>
                </div>
                <Button data-testid="first-comments-backfill-btn" onClick={backfillFirstComments} disabled={commentBusy} variant="outline">
                  {commentBusy ? <RefreshCw size={13} className="mr-1.5 animate-spin" /> : <MessageCircle size={13} className="mr-1.5" />}
                  {commentBusy ? "Posting…" : "Backfill first-comments"}
                </Button>
                {commentResult && (
                  <div className="text-xs text-muted-foreground border rounded-md p-3 bg-muted/40" data-testid="first-comments-result">
                    Scanned <b className="text-foreground">{commentResult.scanned}</b> · Posted <b className="text-accent">{commentResult.updated}</b>
                    {commentResult.orphaned > 0 && <> · Orphans <b className="text-foreground">{commentResult.orphaned}</b></>}
                    {commentResult.failed > 0 && <> · Failed <b className="text-destructive">{commentResult.failed}</b></>}
                    {commentResult.errors?.length > 0 && (
                      <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                        {commentResult.errors.slice(0, 8).map((err, i) => (
                          <li key={i} className="text-[11px] text-destructive font-mono">{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-xl">Channel routing · {selected || "HWG umbrella"}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Toggle which channels each brand publishes to and which brand incoming messages are tagged with. <span className="text-accent font-semibold">Native publishers only — connect Meta, X, LinkedIn, and Threads in the section above.</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {PLATFORMS.map((p) => {
                  const acc = accFor(p.id);
                  return (
                    <div key={p.id} className={`p-4 rounded-xl border flex items-center justify-between gap-3 ${acc ? "border-accent bg-secondary/40" : "border-border bg-muted/40"}`} data-testid={`channel-${p.id}`}>
                      <span className="flex items-center gap-2.5 text-sm font-semibold"><p.icon size={17} /> {p.label}</span>
                      {acc ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-accent text-accent-foreground border-0">Connected</Badge>
                          <Button size="sm" variant="outline" data-testid={`disconnect-${p.id}`} onClick={() => disconnect(acc)}><Unplug size={12} className="mr-1" /> Disconnect</Button>
                        </div>
                      ) : (
                        <Button size="sm" data-testid={`connect-${p.id}`} onClick={() => connect(p.id)}><Plug size={12} className="mr-1" /> Connect</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="identity" className="pt-6">
          {!form ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground text-sm">Select a brand in the sidebar switcher to edit its identity & safety locker. The HWG umbrella identity lives in Brand Knowledge.</CardContent></Card>
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl">{activeBrand.name} · identity & safety locker</CardTitle>
                <Button data-testid="save-identity-btn" onClick={saveIdentity}><Save size={14} className="mr-2" /> Save</Button>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex flex-wrap gap-8">
                  <div className="flex items-center gap-3">
                    <Switch data-testid="brand-active-switch" checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "draft" })} />
                    <Label>{form.status === "active" ? "Active — AI generation enabled" : "Draft — hidden from generation"}</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch data-testid="brand-paused-switch" checked={form.paused} onCheckedChange={(v) => setForm({ ...form, paused: v })} />
                    <Label className="flex items-center gap-1.5"><PauseCircle size={14} /> {form.paused ? "Publishing paused" : "Publishing live"}</Label>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2"><Label>Brand voice rules</Label><Textarea data-testid="identity-voice-input" rows={3} value={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Target audience</Label><Textarea data-testid="identity-audience-input" rows={3} value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Default call to action</Label><Textarea data-testid="identity-cta-input" rows={2} value={form.default_cta} onChange={(e) => setForm({ ...form, default_cta: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Standard disclaimer (auto-added to instructional posts)</Label><Textarea data-testid="identity-disclaimer-input" rows={2} value={form.disclaimer} onChange={(e) => setForm({ ...form, disclaimer: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Tag bank (comma-separated)</Label><Input data-testid="identity-tagbank-input" value={form.tag_bank} onChange={(e) => setForm({ ...form, tag_bank: e.target.value })} /></div>
                <div className="space-y-2">
                  <Label>Sample workout URL <span className="text-muted-foreground text-xs">(Fit Beyond 50 only — the in-browser live workout the coach links to when offering a sample)</span></Label>
                  <Input data-testid="identity-sample-workout-input" type="url" placeholder="https://…" value={form.sample_workout_url} onChange={(e) => setForm({ ...form, sample_workout_url: e.target.value })} />
                </div>
                <div className="space-y-2 rounded-lg border border-dashed border-border p-3 bg-muted/30">
                  <Label className="text-sm">Refresh CTAs on past posts</Label>
                  <p className="text-xs text-muted-foreground">Rewrites ONLY the CTA line and first_comment on past posts so they match the CURRENT brand URL, launch stage and CTA rules above. The rest of every post (body, dialogue, tone, hashtags) is preserved. Runs in the background — you can leave the page.</p>
                  <div className="flex gap-2" data-testid="cta-scope-toggle">
                    {[{v:"companions",l:"Companion posts only"},{v:"all",l:"Every post on this brand"}].map((o)=>(
                      <button key={o.v} type="button" data-testid={`cta-scope-${o.v}`}
                        onClick={()=>setCtaScope(o.v)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${ctaScope===o.v?"bg-accent text-accent-foreground border-accent":"border-border text-muted-foreground hover:text-foreground"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                  <Input data-testid="cta-old-url-input" type="url" placeholder="Optional: paste the OLD URL to swap out (leave blank to just rewrite CTA phrasing)" value={oldUrl} onChange={(e) => setOldUrl(e.target.value)} />
                  <Button data-testid="cta-refresh-btn" variant="outline" size="sm" disabled={ctaBusy || !activeBrand?.id} onClick={refreshCompanionCtas}>
                    <RefreshCw size={14} className={`mr-2 ${ctaBusy ? "animate-spin" : ""}`} />
                    {ctaBusy ? "Refreshing in background…" : `Start refresh — ${activeBrand?.name || "brand"}`}
                  </Button>
                  {ctaJob && (
                    <div className="pt-2 space-y-1.5" data-testid="cta-job-progress">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Job {ctaJob.status === "complete" ? "✓ complete" : ctaJob.status === "running" ? "running…" : ctaJob.status}</span>
                        <span className="font-semibold" data-testid="cta-job-counts">{ctaJob.processed ?? 0} / {ctaJob.total ?? 0} processed · {ctaJob.updated ?? 0} updated</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full transition-all ${ctaJob.status === "complete" ? "bg-emerald-500" : "bg-accent"}`} style={{ width: `${ctaJob.total ? Math.min(100, Math.round(100 * (ctaJob.processed || 0) / ctaJob.total)) : 0}%` }} />
                      </div>
                      {(ctaJob.url_swapped > 0 || ctaJob.failed > 0) && (
                        <p className="text-[11px] text-muted-foreground">{ctaJob.url_swapped || 0} URL swaps · {ctaJob.failed || 0} failed</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2"><Label>Default post time</Label><Input data-testid="identity-posttime-input" type="time" value={form.posting_time} onChange={(e) => setForm({ ...form, posting_time: e.target.value })} /></div>
                </div>
                {form.pillars.length > 0 && (
                  <div className="space-y-3">
                    <Label>Content pillar mix</Label>
                    {form.pillars.map((p, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Input className="flex-1" value={p.name} onChange={(e) => setForm({ ...form, pillars: form.pillars.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                        <Input className="w-20" type="number" value={p.ratio} onChange={(e) => setForm({ ...form, pillars: form.pillars.map((x, j) => (j === i ? { ...x, ratio: Number(e.target.value) } : x)) })} />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Logos & accent colours are managed in Settings → Brands. All rules here are automatically applied to every generated post.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="queue" className="pt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl">Content queue · {selected || "all brands"}</CardTitle>
              <div className="flex gap-3">
                <Button variant="outline" asChild data-testid="queue-calendar-link"><Link to="/calendar">Calendar view</Link></Button>
                <Button asChild data-testid="queue-generate-link"><Link to="/generator"><Sparkles size={14} className="mr-2" /> Generate batch</Link></Button>
              </div>
            </CardHeader>
            <CardContent>
              {activeBrand?.paused && <p className="text-sm text-destructive mb-4 flex items-center gap-2"><PauseCircle size={15} /> Publishing is paused for this brand — scheduled posts will hold until resumed in Identity & safety.</p>}
              {queue.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Nothing queued. Generate a batch to fill the schedule.</p>
              ) : (
                <div className="space-y-2">
                  {queue.slice(0, 15).map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50 border border-border" data-testid={`queue-post-${p.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{p.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">{p.platform} · {new Date(p.scheduled_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                      <Badge variant="secondary" className="capitalize shrink-0">{p.review_status.replace("_", " ")}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="publishlog" className="pt-6">
          <Card>
            <CardHeader><CardTitle className="font-heading text-xl">Publish log · {selected || "all brands"}</CardTitle></CardHeader>
            <CardContent>
              {publog.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Nothing published yet — once posts go out you'll see exactly where each one landed.</p>
              ) : (
                <div className="space-y-2" data-testid="publish-log-list">
                  {publog.map((p) => {
                    const failed = ["native_failed", "real_failed"].includes(p.publish_mode);
                    const real = ["real_native", "real"].includes(p.publish_mode);
                    return (
                      <div key={p.id} className="p-3 rounded-lg bg-muted/50 border border-border" data-testid={`publog-${p.id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{p.title}</p>
                            <p className="text-xs text-muted-foreground capitalize">{p.platform}{p.brand ? ` · ${p.brand}` : ""} · {p.published_at ? new Date(p.published_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {real && <Badge className="bg-accent text-accent-foreground border-0" data-testid={`publog-status-${p.id}`}>Published ✓ · native API</Badge>}
                            {failed && <Badge variant="destructive" data-testid={`publog-status-${p.id}`}>Failed ✕</Badge>}
                            {!real && !failed && <Badge variant="secondary" data-testid={`publog-status-${p.id}`}>Simulated</Badge>}
                            {failed && (
                              <Button size="sm" variant="outline" data-testid={`publog-retry-${p.id}`}
                                onClick={() => api.post(`/posts/${p.id}/retry-publish`).then((r) => { toast.success(r.data.message); loadAll(); api.get("/posts/publish-log", { params: selected ? { brand: selected } : {} }).then((x) => setPublog(x.data)); }).catch((e) => toast.error(errText(e)))}>
                                <RefreshCw size={12} className="mr-1" /> Retry
                              </Button>
                            )}
                          </div>
                        </div>
                        {failed && p.publish_error && <p className="text-xs text-destructive mt-1.5" data-testid={`publog-error-${p.id}`}>{p.publish_error}</p>}
                        {real && p.external_post_id && <p className="text-[11px] text-muted-foreground mt-1">Platform post ID: {p.external_post_id}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="pt-6 space-y-6">
          <OcrCostCard />
          <StorageBackfillCard />
          <PurgeOldMediaCard />
          {perf && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[["Total reach (30d)", perf.totals.reach.toLocaleString(), Eye], ["Followers", perf.totals.followers.toLocaleString(), Users], ["Avg engagement", `${perf.totals.avg_engagement}%`, TrendingUp], ["Avg growth (30d)", `${perf.totals.avg_growth}%`, TrendingUp]].map(([l, v, Icon]) => (
                  <Card key={l}><CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{l}</p><p className="font-heading text-2xl font-semibold mt-1.5">{v}</p></div>
                      <Icon size={16} className="text-muted-foreground" />
                    </div>
                  </CardContent></Card>
                ))}
              </div>
              <Card>
                <CardHeader><CardTitle className="font-heading text-xl">Channel breakdown <span className="text-xs text-accent font-sans font-semibold">(simulated data)</span></CardTitle></CardHeader>
                <CardContent>
                  {perf.channels.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No channels connected for this workspace yet — connect accounts in the first tab to see performance.</p>
                  ) : (
                    <div className="space-y-2">
                      {perf.channels.map((c) => (
                        <div key={c.platform} className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-center p-3 rounded-lg bg-muted/50 text-sm" data-testid={`perf-${c.platform}`}>
                          <span className="font-semibold capitalize">{c.platform}</span>
                          <span>{c.followers.toLocaleString()} <span className="text-xs text-muted-foreground">followers</span></span>
                          <span>{c.reach_30d.toLocaleString()} <span className="text-xs text-muted-foreground">reach</span></span>
                          <span>{c.engagement_rate}% <span className="text-xs text-muted-foreground">eng.</span></span>
                          <span className={c.growth_30d >= 0 ? "text-accent font-semibold" : "text-destructive font-semibold"}>{c.growth_30d > 0 ? "+" : ""}{c.growth_30d}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              {perf.top_posts.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="font-heading text-xl">Top posts</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {perf.top_posts.map((p, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50 text-sm">
                        <span className="truncate font-medium">{p.title}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{p.reach.toLocaleString()} reach · {p.engagement.toLocaleString()} eng.</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="migration" className="pt-6 space-y-6">
          <MigrationAssistantCard />
          <AdminWatermarkCard />
          <MigrationPanel />
        </TabsContent>

        <TabsContent value="notifications" className="pt-6">
          <NotificationsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function OcrCostCard() {
  const [m, setM] = useState(null);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/admin/ocr-metrics", { params: { days: 30 } })
      .then((r) => {
        setM(r.data);
        setBudgetDraft(String(r.data.page_budget || ""));
      }).catch(() => setM(null));
  };
  useEffect(() => { load(); }, []);
  // Persistent 80%-of-budget warning toast — dismissible via OK (patched in App.js).
  useEffect(() => {
    if (!m || !m.page_budget) return;
    const used = m.month_pages_sent_to_llm || 0;
    const ratio = used / m.page_budget;
    if (ratio < 0.8) return;
    const isOver = ratio >= 1;
    const remaining = Math.max(0, m.page_budget - used);
    toast.error(
      isOver
        ? `Gemini OCR budget hit — ${used.toLocaleString()} of ${m.page_budget.toLocaleString()} pages used this month.`
        : `Gemini OCR budget close — ${used.toLocaleString()} of ${m.page_budget.toLocaleString()} pages used (${remaining.toLocaleString()} left, ${Math.round((1 - ratio) * 100)}% remaining).`,
      { id: "ocr-budget-warning" },
    );
  }, [m]);
  const saveBudget = async () => {
    const val = Math.max(0, parseInt(budgetDraft || "0", 10) || 0);
    setSaving(true);
    try {
      await api.put("/admin/ocr-budget", { page_budget: val });
      toast.success(val ? `Monthly page budget set to ${val.toLocaleString()}` : "Monthly page budget cleared");
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };
  if (!m) return null;
  const est = m.est_tokens_saved || 0;
  const monthUsed = m.month_pages_sent_to_llm || 0;
  const budget = m.page_budget || 0;
  const ratio = budget ? Math.min(1, monthUsed / budget) : 0;
  const remaining = Math.max(0, budget - monthUsed);
  const overBudget = budget > 0 && monthUsed >= budget;
  const nearBudget = budget > 0 && !overBudget && ratio >= 0.8;
  const barColor = overBudget ? "bg-destructive" : nearBudget ? "bg-amber-500" : "bg-accent";
  return (
    <Card data-testid="admin-ocr-cost-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImageDown size={18} /> OCR pipeline · last 30 days
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Uploads processed</p>
            <p className="font-heading text-2xl font-semibold mt-1.5" data-testid="admin-ocr-total-events">{m.total_events}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Cache hit rate</p>
            <p className="font-heading text-2xl font-semibold mt-1.5" data-testid="admin-ocr-hit-rate">{m.cache_hit_rate}%</p>
            <p className="text-[11px] text-muted-foreground">{m.cache_hits} of {m.total_events} · {m.cache_size} PDFs cached</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Pages saved from LLM</p>
            <p className="font-heading text-2xl font-semibold mt-1.5 text-accent" data-testid="admin-ocr-pages-saved">{m.pages_saved_from_llm}</p>
            <p className="text-[11px] text-muted-foreground">via Tesseract + cache</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Est. tokens saved</p>
            <p className="font-heading text-2xl font-semibold mt-1.5 text-accent" data-testid="admin-ocr-tokens-saved">{est.toLocaleString()}</p>
            <p className="text-[11px] text-muted-foreground">~1.5k Gemini tokens per page</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Pages sent to Gemini this month: <span className="font-semibold text-foreground">{m.pages_sent_to_llm}</span>. Anything Tesseract or the SHA-256 cache caught first is free — you're only paying for the {m.pages_sent_to_llm} stubborn pages that needed vision-model OCR.
        </p>

        <div className="mt-6 pt-5 border-t border-border space-y-3" data-testid="admin-ocr-budget-block">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold flex items-center gap-2">
                Monthly Gemini page budget
                {overBudget && <Badge variant="destructive" data-testid="admin-ocr-budget-status-over">Over budget</Badge>}
                {nearBudget && <Badge className="bg-amber-500/20 text-amber-700 border-amber-500/40" data-testid="admin-ocr-budget-status-near">Close to limit</Badge>}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Set a cap on Gemini OCR pages for {m.month}. A warning toast fires when you cross 80%.</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="10"
                value={budgetDraft}
                onChange={(e) => setBudgetDraft(e.target.value)}
                placeholder="0 = unlimited"
                className="w-40"
                data-testid="admin-ocr-budget-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={saveBudget}
                disabled={saving || String(m.page_budget || "") === String(parseInt(budgetDraft || "0", 10) || 0)}
                data-testid="admin-ocr-budget-save-btn"
              >{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
          {budget > 0 && (
            <div>
              <div className="h-2 bg-muted rounded-full overflow-hidden" data-testid="admin-ocr-budget-bar">
                <div
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: `${Math.round(ratio * 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="admin-ocr-budget-usage">
                {monthUsed.toLocaleString()} / {budget.toLocaleString()} pages used this month ({Math.round(ratio * 100)}%). {remaining.toLocaleString()} pages left.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function StorageBackfillCard() {
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);
  const [trend, setTrend] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const poll = () => {
    api.get("/admin/storage/backfill/status")
      .then((r) => setData(r.data)).catch(() => setData(null));
  };
  const loadTrend = () => {
    api.get("/admin/storage/disk-trend", { params: { days: 30 } })
      .then((r) => setTrend(r.data)).catch(() => setTrend(null));
  };
  const loadSettings = () => {
    api.get("/admin/storage/settings").then((r) => setSettings(r.data)).catch(() => setSettings(null));
  };
  useEffect(() => {
    poll();
    loadTrend();
    loadSettings();
    // Poll every 3s while a job is running so the progress bar stays live.
    const t = setInterval(() => {
      setData((prev) => {
        if (prev && prev.job && (prev.job.status === "running" || prev.job.status === "queued")) {
          poll();
        }
        return prev;
      });
    }, 3000);
    return () => clearInterval(t);
  }, []);
  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/admin/storage/backfill");
      if (r.data.already_running) {
        toast("Backfill already running — showing live progress.");
      } else {
        toast.success("Backfill started — you can leave this page and come back.");
      }
      poll();
      loadTrend();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setStarting(false);
    }
  };
  const saveAlertSettings = async (patch) => {
    if (!settings) return;
    setSavingSettings(true);
    const next = { ...settings, ...patch };
    try {
      const r = await api.put("/admin/storage/settings", {
        auto_purge_enabled: next.auto_purge_enabled,
        auto_purge_days: next.auto_purge_days,
        purge_email_enabled: next.purge_email_enabled,
        purge_email_recipient: next.purge_email_recipient || null,
        disk_alert_enabled: next.disk_alert_enabled,
        disk_alert_threshold_gb: next.disk_alert_threshold_gb,
        disk_alert_recipient: next.disk_alert_recipient || null,
        slack_webhook_url: next.slack_webhook_url || null,
      });
      setSettings(r.data);
      toast.success("Alert settings saved.");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSavingSettings(false);
    }
  };
  if (!data) return null;
  const { storage_enabled, dry_run, job } = data;
  const running = job && (job.status === "running" || job.status === "queued");
  const done = job && job.status === "done";
  const failed = job && job.status === "failed";
  const total = job?.total || dry_run.total || 0;
  const uploaded = job?.uploaded || 0;
  const pct = total ? Math.min(100, Math.round((uploaded / total) * 100)) : 0;
  const mb = (dry_run.total_bytes / 1024 / 1024).toFixed(1);
  return (
    <Card data-testid="admin-storage-backfill-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <CloudUpload size={18} /> Storage backfill · migration prep
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Push every uploaded file (PDFs, brand logos, generated images, videos, voice samples) into Emergent Object Storage so a freshly-cloned project sees them via the <code className="text-xs px-1 py-0.5 rounded bg-muted">/api/uploads/&lt;file&gt;</code> fallback. Safe to re-run — mirroring is idempotent.
        </p>

        {!storage_enabled && (
          <div className="mb-4 p-3 rounded border border-destructive/40 bg-destructive/5 text-sm text-destructive" data-testid="admin-storage-disabled">
            <AlertTriangle size={14} className="inline mr-1.5" />
            Object Storage not initialized on this pod. Check that EMERGENT_LLM_KEY is set.
          </div>
        )}

        {trend?.series?.length > 1 && (
          <div className="mb-4 p-3 rounded border border-border/60 bg-muted/20" data-testid="admin-storage-trend-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Disk trend · last 30 days</p>
                <p className="font-heading text-lg mt-0.5">
                  {(trend.latest.bytes / 1024 / 1024).toFixed(1)} MB
                  <span className={`text-xs ml-2 ${trend.delta_bytes >= 0 ? "text-amber-600" : "text-accent"}`}>
                    {trend.delta_bytes >= 0 ? "+" : ""}{(trend.delta_bytes / 1024 / 1024).toFixed(1)} MB in 30d
                  </span>
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground">{trend.series.length} daily points · {trend.latest.files} files today</p>
            </div>
            <div className="h-16" data-testid="admin-storage-trend-sparkline">
              <ResponsiveContainer width="99%" height="100%">
                <AreaChart data={trend.series.map((s) => ({ day: s.day, mb: s.bytes / 1024 / 1024 }))} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="diskFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="mb" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#diskFill)" isAnimationActive={false} />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                    formatter={(v) => `${v.toFixed(1)} MB`}
                    labelFormatter={(d) => d}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {settings && (
          <div className="mb-4 p-3 rounded border border-border/60 bg-muted/20 space-y-2" data-testid="admin-disk-alert-block">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.disk_alert_enabled}
                    disabled={savingSettings}
                    onChange={(e) => saveAlertSettings({ disk_alert_enabled: e.target.checked })}
                    className="h-4 w-4 accent-accent"
                    data-testid="disk-alert-enabled-toggle"
                  />
                  <span className="text-sm font-semibold">Alert me when disk crosses…</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1">Sends an email at most once per day when your workspace's daily snapshot goes over the threshold. Uses the same Resend account as the digest emails.</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="500"
                  value={settings.disk_alert_threshold_gb}
                  disabled={savingSettings || !settings.disk_alert_enabled}
                  onChange={(e) => setSettings({ ...settings, disk_alert_threshold_gb: parseFloat(e.target.value) || 0 })}
                  onBlur={(e) => saveAlertSettings({ disk_alert_threshold_gb: parseFloat(e.target.value) || 2.0 })}
                  className="w-24 text-right"
                  data-testid="disk-alert-threshold-input"
                />
                <span className="text-sm text-muted-foreground">GB</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs text-muted-foreground">Email to</Label>
              <Input
                type="email"
                placeholder="owner@… (defaults to purge-report recipient or workspace owner)"
                disabled={savingSettings || !settings.disk_alert_enabled}
                value={settings.disk_alert_recipient || ""}
                onChange={(e) => setSettings({ ...settings, disk_alert_recipient: e.target.value })}
                onBlur={(e) => saveAlertSettings({ disk_alert_recipient: e.target.value || null })}
                className="w-72 flex-1 max-w-md"
                data-testid="disk-alert-recipient-input"
              />
              {trend?.latest && (
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full ${
                    settings.disk_alert_enabled && trend.latest.bytes >= settings.disk_alert_threshold_gb * 1024 * 1024 * 1024
                      ? "bg-destructive/15 text-destructive"
                      : "bg-accent/15 text-accent"
                  }`}
                  data-testid="disk-alert-status-chip"
                >
                  Today: {(trend.latest.bytes / 1024 / 1024 / 1024).toFixed(2)} GB
                </span>
              )}
            </div>
            {settings.last_disk_alert_at && (
              <p className="text-[11px] text-muted-foreground" data-testid="disk-alert-last-sent">
                Last alert: {new Date(settings.last_disk_alert_at).toLocaleString()} at {((settings.last_disk_alert_bytes || 0) / 1024 / 1024 / 1024).toFixed(2)} GB
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="disk-alert-slack-row">
              <Label className="text-xs text-muted-foreground">Slack webhook</Label>
              <Input
                type="url"
                placeholder="https://hooks.slack.com/services/…  (optional, posted alongside email)"
                disabled={savingSettings || !settings.disk_alert_enabled}
                value={settings.slack_webhook_url || ""}
                onChange={(e) => setSettings({ ...settings, slack_webhook_url: e.target.value })}
                onBlur={(e) => saveAlertSettings({ slack_webhook_url: e.target.value || null })}
                className="flex-1 min-w-[240px] max-w-xl font-mono text-xs"
                data-testid="disk-alert-slack-input"
              />
              {settings.slack_webhook_url && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent/15 text-accent">On</span>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Files on disk</p>
            <p className="font-heading text-2xl font-semibold mt-1.5" data-testid="admin-storage-file-count">{dry_run.total}</p>
            <p className="text-[11px] text-muted-foreground">{mb} MB total</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Uploaded</p>
            <p className="font-heading text-2xl font-semibold mt-1.5 text-accent" data-testid="admin-storage-uploaded">{uploaded}</p>
            <p className="text-[11px] text-muted-foreground">{job ? `${pct}% complete` : "not started yet"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Failed</p>
            <p className={`font-heading text-2xl font-semibold mt-1.5 ${job?.failed ? "text-destructive" : ""}`} data-testid="admin-storage-failed">{job?.failed || 0}</p>
            <p className="text-[11px] text-muted-foreground">retry-safe · re-run to resume</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Status</p>
            <p className="font-heading text-2xl font-semibold mt-1.5 flex items-center gap-1.5" data-testid="admin-storage-status">
              {running && <RefreshCw size={16} className="animate-spin text-amber-500" />}
              {done && <Cloud size={16} className="text-accent" />}
              {failed && <AlertTriangle size={16} className="text-destructive" />}
              {job ? job.status : "idle"}
            </p>
            {job?.elapsed_s ? <p className="text-[11px] text-muted-foreground">{job.elapsed_s}s elapsed</p> : null}
          </div>
        </div>

        {job && (
          <div className="mb-4">
            <div className="h-2 bg-muted rounded-full overflow-hidden" data-testid="admin-storage-progress-bar">
              <div
                className={`h-full ${done ? "bg-accent" : failed ? "bg-destructive" : "bg-amber-500"} transition-all`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {job.last_file && (
              <p className="text-[11px] text-muted-foreground mt-1.5 truncate" title={job.last_file}>
                Last file: {job.last_file}
              </p>
            )}
            {job.error && (
              <p className="text-[11px] text-destructive mt-1.5">{job.error}</p>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={start}
            disabled={starting || running || !storage_enabled}
            data-testid="admin-storage-backfill-start-btn"
          >
            {starting ? "Starting…" : running ? "Running…" : done ? "Re-run backfill" : "Start backfill"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={poll}
            data-testid="admin-storage-backfill-refresh-btn"
          >
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <p className="text-[11px] text-muted-foreground ml-auto">
            Run this once before migrating to a new Emergent project.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}




function PurgeOldMediaCard() {
  const [days, setDays] = useState(90);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [history, setHistory] = useState([]);
  const load = (d = days) => {
    setLoading(true);
    api.get("/admin/storage/purge-old-posts/preview", { params: { days: d } })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(errText(e)))
      .finally(() => setLoading(false));
  };
  const loadSettings = () => {
    api.get("/admin/storage/settings").then((r) => setSettings(r.data)).catch(() => setSettings(null));
  };
  const loadHistory = () => {
    api.get("/admin/storage/purge-history", { params: { limit: 5 } })
      .then((r) => setHistory(r.data.rows || [])).catch(() => setHistory([]));
  };
  useEffect(() => {
    load(90);
    loadSettings();
    loadHistory();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  const saveSettings = async (patch) => {
    if (!settings) return;
    setSavingSettings(true);
    const next = { ...settings, ...patch };
    try {
      const r = await api.put("/admin/storage/settings", {
        auto_purge_enabled: next.auto_purge_enabled,
        auto_purge_days: next.auto_purge_days,
        purge_email_enabled: next.purge_email_enabled,
        purge_email_recipient: next.purge_email_recipient || null,
      });
      setSettings(r.data);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSavingSettings(false);
    }
  };
  const doPurge = async () => {
    setPurging(true);
    setConfirmOpen(false);
    try {
      const r = await api.post("/admin/storage/purge-old-posts", { days, confirm: true });
      const freed = (r.data.bytes_freed / 1024 / 1024).toFixed(1);
      toast.success(`Freed ${freed} MB — ${r.data.files_deleted} files across ${r.data.posts_touched} posts.`);
      load(days);
      loadHistory();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setPurging(false);
    }
  };
  const mb = preview ? (preview.bytes / 1024 / 1024).toFixed(1) : "0.0";
  const cutoffLabel = preview?.cutoff_at
    ? new Date(preview.cutoff_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "…";
  const canPurge = preview && preview.posts > 0 && !purging;
  return (
    <Card data-testid="admin-purge-old-media-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <HardDrive size={18} /> Purge old media · disk cleanup
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Delete the image/video/poster files for published posts older than the cutoff. The post row and analytics stay intact — only the on-disk media is removed. Share-tray posts (Denzel pending or shared) are always skipped.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-end">
          <div>
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Cutoff (days)</Label>
            <div className="flex items-center gap-2 mt-2">
              {[30, 60, 90, 180, 365].map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={days === d ? "default" : "outline"}
                  onClick={() => { setDays(d); load(d); }}
                  data-testid={`purge-days-${d}-btn`}
                >{d}d</Button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2" data-testid="purge-cutoff-label">Deleting posts scheduled before <span className="font-semibold text-foreground">{cutoffLabel}</span>.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Posts eligible</p>
            <p className="font-heading text-2xl font-semibold mt-1.5" data-testid="purge-posts-count">{preview?.posts ?? "…"}</p>
            <p className="text-[11px] text-muted-foreground">{preview?.files ?? 0} files</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Disk to free</p>
            <p className="font-heading text-2xl font-semibold mt-1.5 text-accent" data-testid="purge-mb-label">{mb} MB</p>
            <p className="text-[11px] text-muted-foreground">DB rows + analytics untouched</p>
          </div>
        </div>

        {preview && preview.sample.length > 0 && (
          <details className="mb-4" data-testid="purge-sample-details">
            <summary className="text-xs uppercase tracking-widest text-muted-foreground font-semibold cursor-pointer">Preview sample (oldest {preview.sample.length})</summary>
            <ul className="mt-2 text-xs text-muted-foreground space-y-1">
              {preview.sample.map((s) => (
                <li key={s.id} className="flex gap-2 flex-wrap">
                  <span className="font-mono">{new Date(s.scheduled_at).toISOString().slice(0, 10)}</span>
                  <span>·</span>
                  <span>{s.brand || "—"}</span>
                  <span>·</span>
                  <span>{(s.bytes / 1024 / 1024).toFixed(2)} MB</span>
                  <span>·</span>
                  <span className="truncate max-w-[300px]" title={s.files.join(", ")}>{s.files.join(", ")}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="destructive"
                disabled={!canPurge}
                data-testid="purge-open-confirm-btn"
              >
                <Trash2 size={13} className="mr-1.5" />
                {purging ? "Purging…" : preview && preview.posts > 0 ? `Delete ${preview.posts} posts · ${mb} MB` : "Nothing to purge"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="purge-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Purge {preview?.posts} posts' media?</AlertDialogTitle>
                <AlertDialogDescription>
                  This deletes {preview?.files} files (~{mb} MB) from the local disk for posts scheduled before <span className="font-semibold">{cutoffLabel}</span>. The post rows and analytics history stay intact — only image, video, and poster URLs are cleared. Share-tray posts are preserved.
                  <br /><br />
                  <span className="text-destructive font-semibold">This cannot be undone.</span> Object Storage copies remain, but the app will no longer reference them.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="purge-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={doPurge}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="purge-confirm-btn"
                >Yes, delete {mb} MB</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button type="button" variant="outline" size="sm" onClick={() => load(days)} disabled={loading} data-testid="purge-refresh-btn">
            <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {settings && (
          <div className="mt-6 pt-5 border-t border-border space-y-3" data-testid="purge-settings-block">
            <p className="text-sm font-semibold">Automation</p>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.auto_purge_enabled}
                    disabled={savingSettings}
                    onChange={(e) => saveSettings({ auto_purge_enabled: e.target.checked })}
                    className="h-4 w-4 accent-accent"
                    data-testid="auto-purge-enabled-toggle"
                  />
                  <span className="text-sm">Auto-purge monthly</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1">Runs at most once every 30 days. Uses the cutoff below.</p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Cutoff</Label>
                <select
                  value={settings.auto_purge_days || 180}
                  disabled={savingSettings || !settings.auto_purge_enabled}
                  onChange={(e) => saveSettings({ auto_purge_days: parseInt(e.target.value, 10) })}
                  className="px-2 py-1 rounded border border-border bg-background text-sm"
                  data-testid="auto-purge-days-select"
                >
                  {[90, 180, 270, 365, 540].map((d) => <option key={d} value={d}>{d} days</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-start justify-between gap-3 flex-wrap pt-2">
              <div className="flex-1 min-w-[220px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.purge_email_enabled}
                    disabled={savingSettings}
                    onChange={(e) => saveSettings({ purge_email_enabled: e.target.checked })}
                    className="h-4 w-4 accent-accent"
                    data-testid="purge-email-enabled-toggle"
                  />
                  <span className="text-sm">Weekly purge report email (Sun evenings)</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1">Summary of what was purged this week. Defaults to the workspace owner if the recipient is blank.</p>
              </div>
              <Input
                type="email"
                placeholder="owner@…"
                disabled={savingSettings || !settings.purge_email_enabled}
                value={settings.purge_email_recipient || ""}
                onChange={(e) => setSettings({ ...settings, purge_email_recipient: e.target.value })}
                onBlur={(e) => saveSettings({ purge_email_recipient: e.target.value || null })}
                className="w-56"
                data-testid="purge-email-recipient-input"
              />
            </div>
            {settings.last_purge_report_at && (
              <p className="text-[11px] text-muted-foreground pt-1">Last report emailed: {new Date(settings.last_purge_report_at).toLocaleString()}</p>
            )}
            {settings.purge_snoozed_until && new Date(settings.purge_snoozed_until) > new Date() && (
              <div className="mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/30" data-testid="purge-snooze-status">
                <p className="text-[12px] text-amber-800 dark:text-amber-300 font-semibold">
                  ⏰ Snoozed until {new Date(settings.purge_snoozed_until).toLocaleDateString()}
                </p>
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
                  Auto-purge is paused. You'll get a new 24h preview email before it resumes.
                </p>
              </div>
            )}
            {settings.purge_preview_next_run && !settings.purge_snoozed_until && (
              <p className="text-[11px] text-muted-foreground pt-1">
                Next auto-purge: {new Date(settings.purge_preview_next_run).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="purge-history-block">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Recent purges</p>
            <ul className="text-xs space-y-1">
              {history.map((h) => (
                <li key={h.ran_at} className="flex gap-2 flex-wrap text-muted-foreground">
                  <span className="font-mono">{new Date(h.ran_at).toISOString().slice(0, 10)}</span>
                  <span>·</span>
                  <Badge variant="outline" className="text-[10px]">{h.source}</Badge>
                  <span>{h.posts_touched} posts</span>
                  <span>·</span>
                  <span className="text-foreground font-semibold">{(h.bytes_freed / 1024 / 1024).toFixed(1)} MB freed</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function MigrationAssistantCard() {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const poll = () => {
    api.get("/admin/migration/prep-bundle/status")
      .then((r) => setJob(r.data.job)).catch(() => setJob(null));
  };
  useEffect(() => {
    poll();
    const t = setInterval(() => {
      setJob((prev) => {
        if (prev && (prev.status === "running" || prev.status === "queued")) {
          poll();
        }
        return prev;
      });
    }, 3000);
    return () => clearInterval(t);
  }, []);
  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/admin/migration/prep-bundle");
      if (r.data.already_running) {
        toast("Prep already running.");
      } else {
        toast.success("Prep started — backfill first, then bundle build. Grab a coffee.");
      }
      poll();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setStarting(false);
    }
  };
  const running = job && (job.status === "running" || job.status === "queued");
  const done = job && job.status === "done";
  const failed = job && job.status === "failed";
  const backfillPct = job?.backfill_total
    ? Math.min(100, Math.round((job.backfill_uploaded / job.backfill_total) * 100))
    : 0;
  return (
    <Card data-testid="admin-migration-assistant-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Zap size={18} /> Migration assistant · one-click prep
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Prep this project for a full migration to another Emergent workspace: (1) mirror every uploaded file to Object Storage, (2) build a MongoDB + uploads bundle you can download and re-import. Same output as running the shell command — but without SSH.
        </p>

        {job && (
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Phase</p>
              <p className="font-heading text-lg font-semibold mt-1.5 capitalize" data-testid="migration-phase-label">{job.phase}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Backfill</p>
              <p className="font-heading text-lg font-semibold mt-1.5" data-testid="migration-backfill-progress">
                {job.backfill_uploaded || 0} / {job.backfill_total || 0}
              </p>
              <p className="text-[11px] text-muted-foreground">{backfillPct}% files</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Bundle</p>
              <p className="font-heading text-lg font-semibold mt-1.5" data-testid="migration-bundle-size">
                {job.bundle_size_bytes ? `${(job.bundle_size_bytes / 1024 / 1024).toFixed(1)} MB` : "…"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Status</p>
              <p className="font-heading text-lg font-semibold mt-1.5 flex items-center gap-1.5" data-testid="migration-status">
                {running && <RefreshCw size={14} className="animate-spin text-amber-500" />}
                {done && <Cloud size={14} className="text-accent" />}
                {failed && <AlertTriangle size={14} className="text-destructive" />}
                <span className="capitalize">{job.status}</span>
              </p>
            </div>
          </div>
        )}

        {job?.error && (
          <p className="mb-3 text-[12px] text-destructive" data-testid="migration-error">{job.error}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={start}
            disabled={starting || running}
            data-testid="migration-prep-start-btn"
          >
            <Zap size={13} className="mr-1.5" />
            {starting ? "Starting…" : running ? "Running…" : done ? "Re-run prep" : "Start prep"}
          </Button>
          {done && job?.bundle_download_url && (
            <Button asChild variant="outline" size="sm" data-testid="migration-bundle-download-btn">
              <a href={`${process.env.REACT_APP_BACKEND_URL || ""}${job.bundle_download_url}`}>
                <CloudUpload size={13} className="mr-1.5 rotate-180" /> Download bundle
              </a>
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground ml-auto">
            Use this once when spinning up a new Emergent project.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

